/**
 * StatCard Component
 * 
 * Statistics display card with trends
 * 
 * Version: 6.2.0
 */
import React, { ReactNode } from 'react'
import './StatCard.css'

interface StatCardProps {
  title: string
  value: string | number
  icon?: ReactNode
  trend?: {
    value: number
    direction: 'up' | 'down'
    label?: string
  }
  subtitle?: string
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  variant?: 'default' | 'compact' | 'bordered' | 'elevated'
  onClick?: () => void
  className?: string
  children?: ReactNode
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  subtitle,
  color = 'primary',
  variant = 'default',
  onClick,
  className = '',
  children
}) => {
  const trendColors = {
    up: 'var(--success)',
    down: 'var(--danger)'
  }

  const trendIcons = {
    up: '↑',
    down: '↓'
  }

  return (
    <div 
      className={`stat-card ${variant} color-${color} ${onClick ? 'clickable' : ''} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Glow effect for elevated variant */}
      {variant === 'elevated' && <div className="stat-card-glow" />}
      
      {/* Header */}
      <div className="stat-card-header">
        <div className="stat-icon-wrapper">
          {icon && <span className="stat-icon">{icon}</span>}
        </div>
        <span className="stat-title">{title}</span>
      </div>

      {/* Value */}
      <div className="stat-value-wrapper">
        <span className="stat-value">{value}</span>
        {trend && (
          <span 
            className="stat-trend"
            style={{ color: trendColors[trend.direction] }}
          >
            <span className="trend-icon">{trendIcons[trend.direction]}</span>
            <span className="trend-value">{Math.abs(trend.value)}%</span>
            {trend.label && <span className="trend-label">{trend.label}</span>}
          </span>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <span className="stat-subtitle">{subtitle}</span>
      )}

      {/* Custom content */}
      {children && (
        <div className="stat-card-content">
          {children}
        </div>
      )}
    </div>
  )
}

interface MiniStatProps {
  label: string
  value: string | number
  icon?: ReactNode
  color?: string
  className?: string
}

export const MiniStat: React.FC<MiniStatProps> = ({
  label,
  value,
  icon,
  color = 'var(--primary)',
  className = ''
}) => {
  return (
    <div className={`mini-stat ${className}`}>
      {icon && (
        <span className="mini-stat-icon" style={{ color }}>
          {icon}
        </span>
      )}
      <div className="mini-stat-content">
        <span className="mini-stat-value" style={{ color }}>
          {value}
        </span>
        <span className="mini-stat-label">{label}</span>
      </div>
    </div>
  )
}

interface StatGridProps {
  children: ReactNode
  columns?: 2 | 3 | 4
  gap?: 'small' | 'medium' | 'large'
  className?: string
}

export const StatGrid: React.FC<StatGridProps> = ({
  children,
  columns = 4,
  gap = 'medium',
  className = ''
}) => {
  return (
    <div 
      className={`stat-grid columns-${columns} gap-${gap} ${className}`}
    >
      {children}
    </div>
  )
}

export default StatCard